export type ComingSoonCorridorStatus = "coming_soon" | "underway";

export type ComingSoonEcosystemCorridor = {
  id: string;
  sectorSlug: string;
  /** Display label for corridor origin (region or country name). */
  originLabel: string;
  /** Display label for corridor destination (region or country name). */
  targetLabel: string;
  /** ISO-resolvable country for left flag — only when both endpoints are countries. */
  country: string | null;
  /** ISO-resolvable country for right flag — only when both endpoints are countries. */
  targetCountry: string | null;
  /** Second line on the card — corridor focus / theme (e.g. "EV & Components Corridor"). */
  corridorSubtitle: string;
  /** Sector / industry label (used for grouping, not shown on placeholder cards). */
  industryLabel: string;
  status: ComingSoonCorridorStatus;
};

export const COMING_SOON_ECOSYSTEM_CORRIDORS: ComingSoonEcosystemCorridor[] = [
  {
    id: "automotive-europe-india",
    sectorSlug: "automotive-mobility-ecosystem",
    originLabel: "Europe",
    targetLabel: "India",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Automotive Corridor",
    industryLabel: "Automotive & Mobility",
    status: "underway",
  },
  {
    id: "automotive-germany-north-america",
    sectorSlug: "automotive-mobility-ecosystem",
    originLabel: "Germany",
    targetLabel: "North America",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Automotive Supply Chain Corridor",
    industryLabel: "Automotive & Mobility",
    status: "coming_soon",
  },
  {
    id: "automotive-japan-southeast-asia",
    sectorSlug: "automotive-mobility-ecosystem",
    originLabel: "Japan",
    targetLabel: "Southeast Asia",
    country: null,
    targetCountry: null,
    corridorSubtitle: "EV & Components Corridor",
    industryLabel: "Automotive & Mobility",
    status: "coming_soon",
  },
  {
    id: "energy-middle-east-europe",
    sectorSlug: "renewable-energy-electrification-ecosystem",
    originLabel: "Middle East",
    targetLabel: "Europe",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Clean Energy Corridor",
    industryLabel: "Renewable Energy, Electrification & BESS",
    status: "coming_soon",
  },
  {
    id: "energy-north-america-europe",
    sectorSlug: "renewable-energy-electrification-ecosystem",
    originLabel: "North America",
    targetLabel: "Europe",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Battery & Energy Storage Corridor",
    industryLabel: "Renewable Energy, Electrification & BESS",
    status: "coming_soon",
  },
  {
    id: "energy-australia-asia",
    sectorSlug: "renewable-energy-electrification-ecosystem",
    originLabel: "Australia",
    targetLabel: "Asia",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Critical Minerals Corridor",
    industryLabel: "Renewable Energy, Electrification & BESS",
    status: "coming_soon",
  },
  {
    id: "manufacturing-europe-southeast-asia",
    sectorSlug: "industrial-automation-smart-manufacturing-ecosystem",
    originLabel: "Europe",
    targetLabel: "Southeast Asia",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Industrial Manufacturing Corridor",
    industryLabel: "Industrial Automation & Smart Manufacturing",
    status: "coming_soon",
  },
  {
    id: "manufacturing-germany-india",
    sectorSlug: "industrial-automation-smart-manufacturing-ecosystem",
    originLabel: "Germany",
    targetLabel: "India",
    country: "Germany",
    targetCountry: "India",
    corridorSubtitle: "Industrial Automation Corridor",
    industryLabel: "Industrial Automation & Smart Manufacturing",
    status: "coming_soon",
  },
  {
    id: "manufacturing-japan-europe",
    sectorSlug: "industrial-automation-smart-manufacturing-ecosystem",
    originLabel: "Japan",
    targetLabel: "Europe",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Advanced Manufacturing Corridor",
    industryLabel: "Industrial Automation & Smart Manufacturing",
    status: "coming_soon",
  },
];

/** Row 1: origin ↔ target region or country pair. */
export function formatComingSoonCorridorPair(corridor: ComingSoonEcosystemCorridor): string {
  return `${corridor.originLabel} ↔ ${corridor.targetLabel}`;
}

export function getComingSoonCorridorsBySectorSlug(sectorSlug: string): ComingSoonEcosystemCorridor[] {
  return COMING_SOON_ECOSYSTEM_CORRIDORS.filter((corridor) => corridor.sectorSlug === sectorSlug);
}

/** Fallback placeholder only when a sector has no API corridors and no configured corridors. */
export function getGenericLaunchingSoonCorridor(
  sectorSlug: string,
  industryLabel: string,
): ComingSoonEcosystemCorridor {
  return {
    id: `generic-launching-${sectorSlug}`,
    sectorSlug,
    originLabel: "Global",
    targetLabel: "Trade",
    country: null,
    targetCountry: null,
    corridorSubtitle: "Corridor Launching Soon",
    industryLabel,
    status: "coming_soon",
  };
}

export function resolvePlaceholderCorridorsForSector(
  sectorSlug: string,
  industryLabel: string,
  hasRealCorridors: boolean,
): ComingSoonEcosystemCorridor[] {
  const configured = getComingSoonCorridorsBySectorSlug(sectorSlug);
  if (configured.length > 0) {
    return configured;
  }

  if (!hasRealCorridors) {
    return [getGenericLaunchingSoonCorridor(sectorSlug, industryLabel)];
  }

  return [];
}

export function getComingSoonCorridorSectorSlugs(): string[] {
  return [...new Set(COMING_SOON_ECOSYSTEM_CORRIDORS.map((corridor) => corridor.sectorSlug))];
}

/** Flags render only when both endpoints are explicit ISO countries (not regions). */
export function comingSoonCorridorHasFlags(corridor: ComingSoonEcosystemCorridor): boolean {
  return Boolean(corridor.country?.trim() && corridor.targetCountry?.trim());
}
